import React, { useEffect, useLayoutEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { Answer, Button } from 'storybook-ui'

import { Container } from 'components/Container'
import { FooterContainer } from 'components/FooterContainer'
import { OptionType } from 'components/Option'
import { TermsOfUseLink } from 'components/TermsOfUseLink'

import {
  getSubscriptionListAction,
  purchaseUpgradeAction,
} from 'root-redux/actions/common'
import { unsubscribeUserAction } from 'root-redux/actions/user'
import { selectAppName, selectSubscription } from 'root-redux/selects/common'
import {
  selectAnswers,
  selectUUID,
  selectUserCountry,
} from 'root-redux/selects/user'
import { TAppDispatch } from 'root-redux/store/store'

import { useCompatibleRetentionFlowPaymentMethod } from 'hooks/useCompatibleRetentionFlowPaymentMethod'
import { useVatInfo } from 'hooks/useVatInfo'

import { formatPrice } from 'helpers/formatPrice'

import { RetentionStep } from 'modules/unsubscribe/components/RetentionPageContainer/constants'

import { TAnswers } from 'models/common.model'

import { eventLogger } from 'services/eventLogger.service'

import { goTo } from 'browser-history'
import { PageId } from 'page-constants'
import {
  APP_BUTTON_THEMES,
  APP_OPTION_THEMES,
  CURRENCY_SYMBOLS,
  DIRECT_DISCOUNT_OFFER_REASONS,
  EVENT_SOURCE,
  PERSONALIZED_RETENTION_COPY_APPS,
  SubscriptionTagsType,
  UnsubscribeReason,
} from 'root-constants'

import { StyledDiscountOffer as S } from './DiscountOffer.styles'
import {
  APPS_WITHOUT_VAT_INFO_TEXT,
  DEFAULT_DISCOUNT_PERCENTAGE,
  OPTION_VALUES,
  PERSONALIZED_DISCOUNT_REASONS,
} from './constants'

export const DiscountOffer: React.FC = () => {
  const { search } = useLocation()
  const { t } = useTranslation()
  const dispatch: TAppDispatch = useDispatch()
  const appName = useSelector(selectAppName)
  const subscription = useSelector(selectSubscription)
  const userAnswers = useSelector(selectAnswers) as TAnswers
  const userCountry = useSelector(selectUserCountry)
  const userId = useSelector(selectUUID)

  const hasPersonalizedCopy = PERSONALIZED_RETENTION_COPY_APPS.includes(appName)

  const isCompatibleRetentionFlowPaymentMethod =
    useCompatibleRetentionFlowPaymentMethod()

  const [answer, setAnswer] = useState('')

  const descriptionContext =
    hasPersonalizedCopy &&
    PERSONALIZED_DISCOUNT_REASONS.includes(
      userAnswers[PageId.REASONS] as UnsubscribeReason,
    )
      ? `${appName}_${userAnswers[PageId.REASONS]}`
      : appName

  const hasVatInfo = useVatInfo()

  useLayoutEffect(() => {
    const pricesTags = hasVatInfo
      ? `${SubscriptionTagsType.TAX},${SubscriptionTagsType.UNSUBSCRIBE}`
      : `${SubscriptionTagsType.NO_TAX},${SubscriptionTagsType.UNSUBSCRIBE}`

    dispatch(getSubscriptionListAction(pricesTags))
  }, [dispatch, hasVatInfo])

  const discountPercentage = Math.round(
    subscription?.mainPrices.discountPercentage || DEFAULT_DISCOUNT_PERCENTAGE,
  )
  const periodQuantity = subscription?.mainPrices.periodQuantity
  const periodName = subscription?.mainPrices.periodName

  useEffect(() => {
    if (subscription) {
      eventLogger.logDiscountOfferScreenShow(
        `${discountPercentage}% discount on ${periodQuantity}-${periodName}s plan`,
        userId,
      )
    }
  }, [discountPercentage, periodName, periodQuantity, subscription, userId])

  const optionProps = {
    name: PageId.DISCOUNT_OFFER,
    type: OptionType.RADIO,
    theme: APP_OPTION_THEMES[appName],
    onChange: (value: string) => {
      setAnswer(value)
    },
  }

  const handleDeclineAnswer = () => {
    const selectedReason = userAnswers[PageId.REASONS] as UnsubscribeReason
    const directDiscountReasons = DIRECT_DISCOUNT_OFFER_REASONS[appName]

    if (
      directDiscountReasons?.includes(selectedReason) &&
      isCompatibleRetentionFlowPaymentMethod
    ) {
      goTo({
        pathname: PageId.PAUSE_SUBSCRIPTION,
        search,
      })
      return
    }

    dispatch(unsubscribeUserAction())
    goTo({
      pathname: PageId.CANCELED_SUBSCRIPTION,
      search,
    })
  }

  const acceptDiscountOffer = async () => {
    dispatch(purchaseUpgradeAction())
    goTo(
      {
        pathname: PageId.CONTINUED_SUBSCRIPTION,
        search,
      },
      { isUpgrade: true },
    )
  }

  const handleAnswer = () => {
    eventLogger.logDiscountOfferScreenClose(answer, userId)

    if (answer === OPTION_VALUES.NO) {
      handleDeclineAnswer()
      return
    }

    acceptDiscountOffer()
  }

  const getVatLabel = () => {
    switch (userCountry) {
      case 'US':
        return t('retention.discountOffer.inclTaxes')
      case 'CA':
      case 'NZ':
      case 'AU':
        return t('retention.discountOffer.inclGst')
      default:
        return t('retention.discountOffer.inclVat')
    }
  }

  if (!subscription) {
    return null
  }

  return (
    <S.Wrapper step={RetentionStep.THIRD}>
      <Container>
        <S.Description>
          <Trans
            i18nKey="retention.discountOffer.description"
            context={descriptionContext}
            values={{
              percentage: discountPercentage,
            }}
          />
        </S.Description>
        <S.SubscriptionDetails>
          <S.SubscriptionRow>
            <S.SubscriptionDetail>
              {t('retention.discountOffer.newOffer', {
                context: appName,
              })}
            </S.SubscriptionDetail>
            <S.SubscriptionValue>
              {t('subscription.priceAmount', {
                currency: CURRENCY_SYMBOLS[subscription.currency],
                amount: formatPrice(
                  subscription.mainPrices.oldPrices.fullPrice,
                  subscription.currency,
                ),
              })}
            </S.SubscriptionValue>
          </S.SubscriptionRow>
          <S.SubscriptionRow>
            <S.SubscriptionDetail>
              {t('retention.discountOffer.discount', {
                percentage: discountPercentage,
              })}
            </S.SubscriptionDetail>
            <S.DiscountAmount>
              -{' '}
              {t('subscription.priceAmount', {
                currency: CURRENCY_SYMBOLS[subscription.currency],
                amount: formatPrice(
                  subscription.mainPrices.discountAmount,
                  subscription.currency,
                ),
              })}
            </S.DiscountAmount>
          </S.SubscriptionRow>
        </S.SubscriptionDetails>
        <S.NewPrice>
          <span>{t`retention.discountOffer.newPrice`}</span>
          <span>
            {t('subscription.priceAmount', {
              currency: CURRENCY_SYMBOLS[subscription.currency],
              amount: formatPrice(
                subscription.mainPrices.fullPrice,
                subscription.currency,
              ),
            })}
          </span>
        </S.NewPrice>
        {!APPS_WITHOUT_VAT_INFO_TEXT.includes(appName) && (
          <S.VatLabel>{getVatLabel()}</S.VatLabel>
        )}
        <S.Question>
          {t('retention.discountOffer.title', { context: appName })}
        </S.Question>
        <S.OptionsContainer>
          <Answer {...optionProps} value={OPTION_VALUES.YES}>
            <Trans
              i18nKey="retention.discountOffer.yesOffer"
              values={{
                percentage: discountPercentage,
              }}
            />
          </Answer>
          <Answer {...optionProps} value={OPTION_VALUES.NO}>
            {t`retention.discountOffer.noOffer`}
          </Answer>
        </S.OptionsContainer>
        <S.Disclaimer>
          {t`retention.discountOffer.disclaimer`}{' '}
          <TermsOfUseLink
            source={EVENT_SOURCE.DISCOUNT_OFFER}
            text={t`retention.discountOffer.subscriptionTerms`}
          />
        </S.Disclaimer>
      </Container>
      <FooterContainer>
        <Button
          onClick={handleAnswer}
          disabled={!answer}
          theme={APP_BUTTON_THEMES[appName]}
        >
          {t`button.continue`}
        </Button>
        <S.FooterNote>{t`retention.discountOffer.discountStart`}</S.FooterNote>
      </FooterContainer>
    </S.Wrapper>
  )
}
